/**
 * SPEC §5.3 — the stopping rule, as a pure function of a sample matrix.
 * Nothing here touches the network; the client feeds it timings.
 */
import type { Confidence } from './weights.ts';

export interface CandidateSamples {
  code: string;
  /** timed samples in ms; Infinity for a request that aborted, failed or had the wrong status */
  samples: number[];
}

export type Decision =
  | {
      kind: 'winner';
      code: string;
      rttMs: number;
      confidence: Confidence;
      marginMs: number;
      jitterMs: number;
      /** true when the leader shared its score with another candidate and was picked by lot */
      coinFlip: boolean;
    }
  | { kind: 'all_failed' };

export function score(c: CandidateSamples): number {
  return c.samples.length ? Math.min(...c.samples) : Infinity;
}

function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** Jitter: median over candidates with ≥2 finite samples of (max − min). */
export function jitter(cands: CandidateSamples[]): number {
  const spreads: number[] = [];
  for (const c of cands) {
    const finite = c.samples.filter(Number.isFinite);
    if (finite.length >= 2) spreads.push(Math.max(...finite) - Math.min(...finite));
  }
  return median(spreads);
}

/**
 * Every run with at least one surviving candidate names a winner: the lowest
 * score. The margin over the runner-up, against the run's own noise floor,
 * decides only how much the vote is worth (`clear` or `marginal`), never
 * whether it is cast. Candidates with exactly equal scores are separated by
 * `rng`, injectable so the flip is testable.
 */
export function decide(cands: CandidateSamples[], rng: () => number = Math.random): Decision {
  const ranked = cands.map((c) => ({ c, s: score(c) })).filter((x) => Number.isFinite(x.s)).sort((a, b) => a.s - b.s);
  if (ranked.length === 0) return { kind: 'all_failed' };
  const top = ranked[0]!.s;
  const tied = ranked.filter((x) => x.s === top);
  const best = tied.length > 1 ? tied[Math.min(tied.length - 1, Math.floor(rng() * tied.length))]! : ranked[0]!;
  const second = ranked.find((x) => x !== best);
  const J = jitter(cands);
  // A lone survivor has no rival to be confused with; treat its margin as unbounded.
  const M = second ? second.s - best.s : Infinity;
  const confidence: Confidence = M >= Math.max(20, 2 * J) ? 'clear' : 'marginal';
  return { kind: 'winner', code: best.c.code, rttMs: best.s, confidence, marginMs: M, jitterMs: J, coinFlip: tied.length > 1 };
}

/** Round 2 may stop early once the leader already clears the `clear` threshold. */
export function leaderClears(cands: CandidateSamples[]): boolean {
  const d = decide(cands);
  return d.kind === 'winner' && d.confidence === 'clear';
}
